import { useRef, useState } from 'react'
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDelayGroup,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions,
} from '@floating-ui/react'

import { EMPTY_OBJECT, SYMBOL_HYPHEN } from '../../constants/common'

import {
  FLOATING_TOOLTIP_DELAY_CONFIG,
  FLOATING_TOOLTIP_DISMISS_PARAMS,
} from './useFloatingTooltip.constants'

export function useFloatingTooltip({
  disabled,
  dismissParams = FLOATING_TOOLTIP_DISMISS_PARAMS,
  fallbackPlacements,
  hoverConfig: {
    delay = FLOATING_TOOLTIP_DELAY_CONFIG,
    enabled = !disabled,
    ...hoverConfig
  } = EMPTY_OBJECT,
  isOpenOnClick = false,
  placement,
}) {
  const nodeId = useFloatingNodeId()

  const [isOpen, setOpen] = useState(false)

  const arrowRef = useRef(null)

  // https://floating-ui.com/docs/flip#combining-with-shift
  const overflowMiddleware = placement?.includes(SYMBOL_HYPHEN)
    ? [flip({ fallbackPlacements }), shift()]
    : [shift(), flip({ fallbackPlacements })]

  // floating-ui's arrow middleware expects the ref object itself, not its
  // value; it reads `.current` later, outside render.
  const middleware = [
    offset(10),
    ...overflowMiddleware,
    // eslint-disable-next-line react-hooks/refs
    arrow({ element: arrowRef }),
  ]

  const { context, floatingStyles, refs } = useFloating({
    middleware,
    nodeId,
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
    onOpenChange: setOpen,
  })

  // Inside a `FloatingDelayGroup` this tooltip closes the one opened before it.
  useDelayGroup(context)

  const hover = useHover(context, {
    ...hoverConfig,
    delay,
    enabled,
  })

  const click = useClick(context, {
    enabled: isOpenOnClick && !disabled,
  })

  const dismiss = useDismiss(context, {
    ...dismissParams,
    referencePress: dismissParams.referencePress && !isOpenOnClick,
  })

  const { getReferenceProps } = useInteractions([dismiss, hover, click])

  return {
    arrowRef,
    context,
    floatingStyles,
    getReferenceProps,
    isOpen,
    nodeId,
    refs,
  }
}
